import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { toast } from '@/lib/toast';
import { useTranslation } from 'react-i18next';
import { useDataStore } from '@/stores/dataStore';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import { jumpStep, shouldLoadOlder } from '@/lib/historyPaging';
import type { GuildMemberRow, GuildRole, Message } from '@/types/api';
import { buildMessageRows, computeUnreadIndex, type RowItem } from './messageRows';
import type { SavedChannelKind } from '@/lib/savedMessages';
import { indexRolesById } from '@/lib/roleColor';
import { canIn, computeGuildPermissions } from '@/lib/guildPermissions';
import { useGuildBans } from '@/lib/useGuildBans';
import { PERMISSIONS } from '@/lib/permissions';
import { hasPermission } from '@/components/serverSettings/permissionConstants';
import { EmptyChannel } from '@/components/contentPane/EmptyChannel';
import { canPinIn } from './messagePermissions';
import { emitAppEvent, onAppEvent } from '@/lib/appEvents';
import {
  registerPendingDelete,
  clearPendingDelete,
  cancelPendingDelete,
} from './messagePendingDeletes';
import { VirtualRow } from './MessageRow';
import { useHistoryScrollAnchor } from './useHistoryScrollAnchor';
import { JumpToLatest } from './JumpToLatest';

// Stable empty-messages reference. Without this, `messages ?? []` at the
// callsite produced a fresh `[]` literal every render, which invalidated
// every downstream useMemo([messages]) and forced the virtualizer to
// rebuild on each ContentPane render.
export const EMPTY_MESSAGE_LIST: Message[] = [];

export interface MessagesListProps {
  channelId: string;
  /** Complete floating dock height; zero for the ordinary stacked layout. */
  bottomInset?: number;
  messages: Message[];
  /** History fetch still in flight (no cached messages yet) — show skeleton
   *  rows instead of the "start of conversation" empty state. */
  loading?: boolean;
  /** The first history page failed. Keep the recovery action in the chat. */
  loadError?: boolean;
  onRetry?: () => void;
  myId: string | null;
  channelName: string;
  /** Channel kind threaded into saved-message bookmarks so the saved
   *  view can route back here with the right pane (text vs DM vs group). */
  channelKind: SavedChannelKind;
  lastReadId: string | null;
  members: GuildMemberRow[];
  roles: GuildRole[];
  onReply: (m: Message) => void;
  /** This channel is the user's personal "Сейф" (self-chat) — tailors the
   *  empty state (no @-mention hint, a "what is this" blurb). */
  isVault?: boolean;
  /** Ask for an older page. Called when the reader nears the top of the loaded
   *  history — until this existed, a channel's past ended at the first 50
   *  messages and there was no way to reach anything before them. */
  onLoadOlder?: () => void | Promise<boolean>;
  /** An older page is in flight — the top row says so and the trigger holds. */
  loadingOlder?: boolean;
  /** The oldest loaded message really is the first one, so stop asking. */
  atStart?: boolean;
}

export function MessagesList(props: MessagesListProps) {
  const { t } = useTranslation();
  const { channelName, channelKind, lastReadId, messages, myId, members, roles, onReply } = props;
  const bottomInset = props.bottomInset ?? 0;
  const trailingSpace = bottomInset > 0 ? bottomInset + 8 : 0;
  const { onLoadOlder, loadingOlder = false, atStart = false } = props;
  const [editRequest, setEditRequest] = useState<{ id: string; version: number } | null>(null);
  useEffect(() => onAppEvent('chat:edit-last', ({ channelId }) => {
    if (channelId !== props.channelId) return;
    const target = messages.find((m) => m.authorId === myId && !m.system && !m.poll && !m.taskList && !!m.content.trim() && !m.id.startsWith('tmp-'));
    if (!target) return;
    setEditRequest((previous) => ({ id: target.id, version: (previous?.version ?? 0) + 1 }));
    emitAppEvent('chat:scroll-to-message', { messageId: target.id });
  }), [props.channelId, messages, myId]);
  const consumeEditRequest = useCallback(() => setEditRequest(null), []);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { capture: captureHistory, restore: restoreHistory, cancel: cancelHistory, restoring: restoringHistory } = useHistoryScrollAnchor(scrollRef);
  // newest-first in store; render reversed for natural top→bottom chat order.
  const ordered = useMemo(() => [...messages].reverse(), [messages]);
  const upsertMessage = useDataStore((s) => s.upsertMessage);
  const removeMessage = useDataStore((s) => s.removeMessage);
  const rolesById = useMemo(() => indexRolesById(roles), [roles]);
  // Subscribed selectors (NOT getState()) so moderation rights and the
  // user-card guild context recompute when the active server changes. Reading
  // getState() inside the memo froze the value at first render → stale
  // moderation (foreign-delete shown for the wrong guild) after a switch.
  const currentGuildId = useDataStore((s) => s.currentGuildId);
  const currentGuild = useDataStore((s) => (s.currentGuildId ? s.guilds[s.currentGuildId] : undefined));
  const dmChannel = useDataStore((s) => s.dmChannels[props.channelId]);
  // My permissions in this guild (@everyone baseline + roles, like the server).
  const guildPermissions = useMemo(() => {
    if (!myId || !currentGuild) return 0n;
    // The server's answer, when it sent one; else from the loaded roster.
    if (currentGuild.myPermissions !== undefined) return BigInt(currentGuild.myPermissions);
    const me = members.find((mm) => mm.userId === myId) ?? null;
    return computeGuildPermissions({
      member: me,
      rolesById,
      isOwner: currentGuild.ownerId === myId,
      guildId: currentGuild.id,
    });
  }, [members, rolesById, myId, currentGuild]);
  // May I delete other people's messages here? Drives the foreign "Удалить".
  const canModerate = hasPermission(guildPermissions, PERMISSIONS.MANAGE_MESSAGES);
  const canPin = canPinIn({ channelKind, guildPermissions, dmChannel, myId });
  // Banned-author treatment (badge + collapsed messages). The ban list is
  // moderator-gated server-side, so only fetch it for moderators.
  const { bannedIds } = useGuildBans(currentGuildId, { enabled: canModerate });

  // Comment-threads are off in 1:1 DMs always, and toggleable per channel for
  // TEXT/ANNOUNCE/GROUP_DM. Read from the subscribed channel so the affordance
  // reacts to a settings change — the old selector walked every channel of
  // every guild on each store update (every message, typing and presence).
  const channelSettings = dmChannel ?? currentGuild?.channels.find((c) => c.id === props.channelId);
  const commentsAllowed = channelKind !== 'DM' && channelSettings?.commentsEnabled !== false;
  const memberByUserId = useMemo(
    () => new Map(members.map((m) => [m.userId, m])),
    [members],
  );
  // Resolve a reactor's id → display name for the reaction-pill tooltip
  // ("кто поставил"). Guild channels have the member roster; in DMs the map is
  // empty and the pill falls back to a generic hint (you know who's there).
  const reactionNameFor = useMemo(
    () => (userId: string): string | undefined => {
      const mem = memberByUserId.get(userId);
      if (!mem) return undefined;
      return mem.nickname || mem.user.displayName || mem.user.username;
    },
    [memberByUserId],
  );

  const unreadIdx = useMemo(
    () => computeUnreadIndex(ordered, lastReadId),
    [ordered, lastReadId],
  );

  // Flat row descriptors (day / unread / grouped messages) for the virtualizer.
  // Grouping + divider-merge logic lives in the pure, tested messageRows module.
  const items = useMemo<RowItem[]>(() => buildMessageRows(ordered, unreadIdx), [ordered, unreadIdx]);

  // Row action handlers — stabilized with useCallback so the memoized
  // <VirtualRow> only re-renders when its own message/display props change, not
  // on every parent render (typing, presence, scroll-driven setState, …). Each
  // takes the message as an argument, so the closures capture only stable refs
  // (store actions, api, t) — and `myId` for the reaction's own-vote check.
  const onEdit = useCallback(
    async (m: Message, content: string) => {
      try {
        const updated = await api.editMessage(m.id, { content });
        upsertMessage(updated);
      } catch (err) {
        toast.error(...errToToast(err, t('messages.saveError')));
        throw err;
      }
    },
    [t, upsertMessage],
  );
  // Soft delete with sonner-undo. Pull the message immediately (instant UI),
  // then schedule the API call after 5s; "Отменить" cancels the timer and
  // restores the bubble. Timers live in a module-level registry so a channel
  // switch still resolves the delete even after the toast unmounts.
  const onDelete = useCallback(
    async (m: Message) => {
      removeMessage(m.channelId, m.id);
      const timer = window.setTimeout(async () => {
        clearPendingDelete(m.id);
        try {
          await api.deleteMessage(m.id);
        } catch (err) {
          upsertMessage(m);
          toast.error(...errToToast(err, t('messages.deleteError')));
        }
      }, 5_000);
      registerPendingDelete(m.id, { timer, restore: () => upsertMessage(m) });
      toast(t('messages.deleted'), {
        description: t('messages.undoHint'),
        duration: 5_000,
        action: { label: t('common.undo'), onClick: () => cancelPendingDelete(m.id) },
      });
    },
    [t, removeMessage, upsertMessage],
  );
  // "Delete for me" — hide from my own view (any message). No undo: the server
  // records the hide; the row is pulled immediately and restored on failure.
  const onDeleteForMe = useCallback(
    async (m: Message) => {
      removeMessage(m.channelId, m.id);
      try {
        await api.deleteMessage(m.id, 'me');
      } catch (err) {
        upsertMessage(m);
        toast.error(...errToToast(err, t('messages.deleteError')));
      }
    },
    [t, removeMessage, upsertMessage],
  );
  const onTogglePin = useCallback(
    async (m: Message) => {
      try {
        const updated = m.pinned ? await api.unpinMessage(m.id) : await api.pinMessage(m.id);
        upsertMessage(updated);
        // Keep PinnedBar / PinnedMessagesButton in sync with the mutation.
        emitAppEvent('chat:pins-changed');
      } catch (err) {
        toast.error(...errToToast(err, t('messages.pinError')));
      }
    },
    [t, upsertMessage],
  );
  // In a server channel the server says whether I may react here (a DM
  // carries no such field: always yes, the server decides).
  const canReact = canIn(channelSettings, PERMISSIONS.ADD_REACTIONS);
  const onReact = useCallback(
    async (m: Message, emoji: string) => {
      const has = m.reactions.some(
        (g) => g.emoji === emoji && myId !== null && g.users.includes(myId),
      );
      // Taking my own reaction back is always allowed; adding one needs the right.
      if (!has && !canReact) {
        toast.error(t('messages.reactionNotAllowed'));
        return;
      }
      try {
        if (has) await api.removeReaction(m.id, emoji);
        else await api.addReaction(m.id, emoji);
      } catch (err) {
        toast.error(...errToToast(err, t('messages.reactionError')));
      }
    },
    [t, myId, canReact],
  );

  // Dynamic-size virtualizer. Estimate is the average tight bubble height —
  // measureElement refines it as items mount. We keep a generous overscan
  // (12 rows) so quick mouse-wheel flicks don't reveal placeholders.
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 56,
    overscan: 12,
    paddingEnd: trailingSpace,
    scrollPaddingEnd: trailingSpace,
    getItemKey: (idx) => items[idx]?.key ?? idx,
  });

  // Stick-to-bottom on new messages. Only auto-scroll when the user is
  // already near the bottom (within ~64px) — otherwise we'd yank them
  // away while they're reading history.
  const stickToBottomRef = useRef(true);
  // Show the jump-to-latest button once the user has scrolled meaningfully up.
  // setState is a no-op re-render when the value is unchanged, so calling it on
  // every scroll tick is cheap.
  const [showJump, setShowJump] = useState(false);
  // A programmatic jump keeps its target stable until the reader scrolls.
  const jumpScrolling = useRef(false);
  const touchY = useRef<number | null>(null);
  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 64;
    setShowJump(distanceFromBottom > 220);
    // Near the top and there's more behind it → fetch the previous page.
    if (!restoringHistory.current && !jumpScrolling.current && !pendingJumpRef.current && shouldLoadOlder({
      scrollTop: el.scrollTop,
      loading: loadingOlder,
      atStart,
      canLoad: !!onLoadOlder,
    })) {
      const release = captureHistory();
      if (release) {
        void Promise.resolve().then<boolean | void>(() => onLoadOlder!()).then((loaded) => {
          if (loaded === false) release();
        }, release);
      }
    }
  }

  // Restore after page completion, including failed/empty requests.
  useLayoutEffect(() => {
    if (!loadingOlder) restoreHistory();
  }, [messages, loadingOlder, restoreHistory]);
  function jumpToBottom() {
    cancelHistory();
    stickToBottomRef.current = true;
    if (items.length === 0) return;
    virtualizer.scrollToIndex(items.length - 1, { align: 'end', behavior: 'smooth' });
  }
  // Depend on `items` (reference) so we re-stick to bottom on edits +
  // deletes too, not only on count changes. Keeps the chat anchored even
  // when an image bubble re-measures and grows after load.
  //
  // The pin runs THREE times across animation frames: the first scroll is
  // computed against the virtualizer's 56px row ESTIMATE, but tall bubbles
  // (images, task-lists, voice notes) measure a frame or two later and grow
  // the content above the anchor — without the re-pins a freshly-opened chat
  // lands mid-history instead of on the latest message. Re-running scrollToIndex
  // after each measurement pass re-anchors to the true bottom.
  useEffect(() => {
    if (items.length === 0) return;
    // Guarded so the delayed re-pins below no-op if the user scrolled up in the
    // meantime (voice notes/images can take ~400ms to measure their height).
    const pin = () => {
      if (stickToBottomRef.current) {
        virtualizer.scrollToIndex(items.length - 1, { align: 'end' });
      }
    };
    if (!stickToBottomRef.current) return;
    pin();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      pin();
      raf2 = requestAnimationFrame(pin);
    });
    // Late-measuring rows (waveforms, images) grow the content above the anchor
    // a few frames after mount — re-anchor to the true bottom so a freshly
    // opened chat lands on the newest message, not mid-history.
    const t1 = window.setTimeout(pin, 160);
    const t2 = window.setTimeout(pin, 480);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [items, virtualizer]);

  const viewportHeight = virtualizer.scrollRect?.height ?? 0;
  // Dock growth must follow only an already-attached reader. Keeping the
  // scroller full-height leaves an older-history reader's scrollTop unchanged.
  useLayoutEffect(() => {
    if (stickToBottomRef.current && items.length > 0) {
      virtualizer.scrollToIndex(items.length - 1, { align: 'end' });
    }
  }, [trailingSpace, viewportHeight, items.length, virtualizer]);

  // Jump-to-message bus — search popover / pin popover dispatch a
  // CustomEvent('chat:scroll-to-message', { detail: { messageId } }) and
  // we scroll the row into view + flash it for 1.4s so the user can find
  // the result visually after the popover closes.
  //
  // itemsRef avoids re-binding the window listener on every message
  // arrival — `items` is recreated whenever a message is added/edited,
  // so depending on it had us tearing the listener down ~once per second
  // in busy channels (briefly losing dispatches mid-rebind).
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const flashFrame = useRef<number | null>(null);
  const flashTimer = useRef<number | null>(null);
  const flashedNode = useRef<HTMLElement | null>(null);
  const clearFlash = useCallback(() => {
    if (flashFrame.current !== null) cancelAnimationFrame(flashFrame.current);
    if (flashTimer.current !== null) window.clearTimeout(flashTimer.current);
    flashedNode.current?.classList.remove('chat-flash');
    flashFrame.current = null;
    flashTimer.current = null;
    flashedNode.current = null;
  }, []);
  useEffect(() => clearFlash, [clearFlash]);
  const indexOfMessage = useCallback(
    (id: string) => itemsRef.current.findIndex((it) => it.kind === 'message' && it.key === id),
    [],
  );
  const scrollToLoaded = useCallback(
    (id: string, idx: number) => {
      clearFlash();
      jumpScrolling.current = true;
      cancelHistory();
      stickToBottomRef.current = false;
      const availableHeight = (scrollRef.current?.clientHeight ?? 0) - trailingSpace;
      const oversized = (virtualizer.measurementsCache[idx]?.size ?? 0) > availableHeight;
      virtualizer.scrollToIndex(idx, { align: trailingSpace > 0 ? (oversized ? 'start' : 'end') : 'center' });
      // The virtualizer mounts the row a frame or two after scrolling to it
      // (longer when it has to measure first), so look for it a few frames.
      let tries = 0;
      const flash = () => {
        const node = scrollRef.current?.querySelector(`[data-message-id="${id}"]`) as HTMLElement | null;
        if (!node) {
          if (++tries < 8) flashFrame.current = requestAnimationFrame(flash);
          else clearFlash();
          return;
        }
        // An offscreen row may still have its estimate when the jump starts.
        // Once mounted, reveal the beginning of a tall target unless the user
        // has already taken over scrolling.
        if (trailingSpace > 0 && jumpScrolling.current && node.getBoundingClientRect().height > availableHeight) {
          virtualizer.scrollToIndex(idx, { align: 'start' });
        }
        node.classList.add('chat-flash');
        flashedNode.current = node;
        flashTimer.current = window.setTimeout(clearFlash, 1400);
      };
      flashFrame.current = requestAnimationFrame(flash);
    },
    [virtualizer, clearFlash, trailingSpace, cancelHistory],
  );

  // A target above the loaded history (a reply to an old message, an old pin
  // or search hit) used to do nothing at all. Now the list pages back for it,
  // one page at a time through the normal paging path, and says so when it
  // can't be reached.
  const pendingJumpRef = useRef<{ id: string; pages: number; waiting?: boolean } | null>(null);
  const jumpFrame = useRef<number | null>(null);
  useEffect(() => () => {
    pendingJumpRef.current = null;
    if (jumpFrame.current !== null) cancelAnimationFrame(jumpFrame.current);
  }, []);
  const loadOlderRef = useRef(onLoadOlder);
  loadOlderRef.current = onLoadOlder;
  const advanceJump = useCallback(() => {
    const pending = pendingJumpRef.current;
    if (!pending || pending.waiting) return;
    const idx = indexOfMessage(pending.id);
    const step = jumpStep({
      found: idx >= 0,
      loading: loadingOlder,
      atStart,
      canLoad: !!loadOlderRef.current,
      pagesLoaded: pending.pages,
    });
    if (step === 'scroll') {
      pendingJumpRef.current = null;
      scrollToLoaded(pending.id, idx);
    } else if (step === 'load') {
      pending.pages += 1;
      stickToBottomRef.current = false;
      pending.waiting = true;
      void Promise.resolve(loadOlderRef.current?.()).then((ok) => {
        if (pendingJumpRef.current !== pending) return;
        pending.waiting = false;
        if (ok === false) pendingJumpRef.current = null;
        else jumpFrame.current = requestAnimationFrame(() => advanceJumpRef.current());
      }, () => { if (pendingJumpRef.current === pending) pendingJumpRef.current = null; });
    } else if (step === 'missing') {
      pendingJumpRef.current = null;
      toast.info(t('messages.jumpTargetMissing'));
    }
  }, [indexOfMessage, scrollToLoaded, loadingOlder, atStart, t]);
  const advanceJumpRef = useRef(advanceJump);
  advanceJumpRef.current = advanceJump;
  // Each landed page (or the end of history) moves a pending jump on.
  useEffect(() => {
    advanceJumpRef.current();
  }, [items, loadingOlder, atStart]);

  useEffect(() => {
    return onAppEvent('chat:scroll-to-message', ({ messageId: id }) => {
      if (!id) return;
      pendingJumpRef.current = { id, pages: 0 };
      advanceJumpRef.current();
    });
  }, []);

  if (ordered.length === 0) {
    return (
      <div ref={scrollRef} data-chat-scroller className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-4 py-4" style={bottomInset > 0 ? { paddingBottom: trailingSpace } : undefined}>
        {props.loadError ? (
          <div className="flex min-h-full flex-col items-center justify-center gap-3 py-12 text-center" role="alert">
            <p className="text-sm font-medium text-ink">{t('messages.loadFailed')}</p>
            <button
              type="button"
              onClick={props.onRetry}
              className="min-h-11 rounded-xl bg-accent px-5 text-sm font-semibold text-bg transition-colors hover:bg-accent-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              {t('common.retry')}
            </button>
          </div>
        ) : props.loading ? (
          // History still loading — skeleton rows so the pane reads as "loading",
          // not as an empty "start of conversation" (which then flashes away).
          <div className="flex flex-col gap-5 pt-2" aria-hidden>
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="flex gap-3">
                <div className="size-9 shrink-0 animate-pulse rounded-full bg-default/50" />
                <div className="flex-1 space-y-2 py-1">
                  <div className="h-3 w-28 animate-pulse rounded bg-default/50" />
                  <div
                    className="h-3 animate-pulse rounded bg-default/40"
                    style={{ width: `${38 + ((i * 17) % 52)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyChannel name={channelName} kind={channelKind} isVault={props.isVault} />
        )}
      </div>
    );
  }

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  return (
    // Adaptive width: full-width up to and including 2K (2560px) windows, so
    // the conversation uses the available space; only on a true ultrawide
    // (window ≥ 2700px, e.g. a 34" 3440px) cap + centre the column so it (and
    // the jump-to-latest pill inside it) doesn't strand against the far edge.
    <div className="studio-timeline relative mx-auto flex min-h-0 w-full flex-1 flex-col min-[2700px]:max-w-[2000px]">
    <div
      ref={scrollRef} data-chat-scroller
      onScroll={onScroll}
      onPointerDown={(event) => {
        // Native scrollbar/track interaction does not emit a wheel event.
        if (event.target === event.currentTarget) { jumpScrolling.current = false; cancelHistory(); }
      }}
      onWheel={(event) => {
        cancelHistory();
        jumpScrolling.current = false;
        // At the top, wheel-up may not produce a native scroll event.
        if (event.deltaY < 0) onScroll();
      }}
      onTouchStart={(event) => { cancelHistory(); touchY.current = event.touches[0]?.clientY ?? null; }}
      onTouchMove={(event) => {
        jumpScrolling.current = false;
        const y = event.touches[0]?.clientY;
        if (y !== undefined && touchY.current !== null && y > touchY.current) onScroll();
      }}
      onKeyDown={(event) => {
        if (['ArrowUp', 'PageUp', 'Home', 'ArrowDown', 'PageDown', 'End'].includes(event.key)) {
          cancelHistory();
          jumpScrolling.current = false;
          if (['ArrowUp', 'PageUp', 'Home'].includes(event.key)) onScroll();
        }
      }}
      className="scrollbar-thin flex min-h-0 flex-1 flex-col overflow-y-auto pb-3"
      style={bottomInset > 0 ? { paddingBottom: 0, scrollPaddingBottom: trailingSpace } : undefined}
    >
      {/* Outer spacer matches the total virtual height; inner absolute
          layer carries the actually-rendered rows translated into place.
          padding-x lives on the row wrapper so the scrollbar sits flush
          with the column edge. Virtual trailing space reserves the floating
          dock; the stacked layout keeps its original pb-3 gap. */}
      <div className="mt-auto shrink-0" style={{ height: totalSize, width: '100%', position: 'relative' }}>
        {virtualItems.map((virtual) => {
          const item = items[virtual.index];
          if (!item) return null;
          return (
            <div
              key={item.key}
              data-index={virtual.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                transform: `translateY(${virtual.start}px)`,
              }}
              className="chat-row-wrapper"
            >
              <VirtualRow
                item={item}
                myId={myId}
                channelLabel={channelName}
                channelKind={channelKind}
                isVault={props.isVault}
                commentsAllowed={commentsAllowed}
                reactionNameFor={reactionNameFor}
                canModerate={canModerate}
                canPin={canPin}
                authorBanned={item.kind === 'message' && bannedIds.has(item.message.authorId)}
                onReply={onReply}
                editRequest={editRequest}
                onEditRequestHandled={consumeEditRequest}
                onEdit={onEdit}
                onDelete={onDelete}
                onDeleteForMe={onDeleteForMe}
                onTogglePin={onTogglePin}
                onReact={onReact}
              />
            </div>
          );
        })}
      </div>
    </div>
      {loadingOlder && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center py-2 text-[12px] text-ink-muted">
          <span className="rounded-full bg-surface px-3 py-1">{t('messages.loadingOlder')}</span>
        </div>
      )}
      <JumpToLatest bottomInset={bottomInset} show={showJump} onClick={jumpToBottom} />
    </div>
  );
}
